import {
    faCalendarPlus, faCalendarXmark, faClock, faPhone, faSquareCheck, faUserTag, faUserXmark,
} from '@fortawesome/free-solid-svg-icons';

export const getEmployeeName = (emp) => {
    if (emp.employee) return emp.employee;
    if (emp.lastName || emp.firstName) {
        return [emp.lastName, emp.firstName].filter(Boolean).join(', ');
    }
    return '';
};

export const getTypeaheadValue = (item) => item?.value ?? null;
export const getTypeaheadLabel = (item) => item?.label ?? '';

export const buildEmployeeSet = (list) => new Set(
    (Array.isArray(list) ? list : [])
        .map((entry) => getTypeaheadValue(entry))
        .filter((value) => value != null),
);

export const hasEmployeeInLookup = (lookup, employeeId) => {
    if (!lookup || employeeId == null) {
        return false;
    }

    return lookup.some((item) => item.value === employeeId);
};

export const collectTypeLabelsForEmployee = (types, lookup, employeeId) => {
    if (!Array.isArray(types) || !lookup || employeeId == null) {
        return [];
    }

    const labels = [];

    types.forEach((type) => {
        const key = getTypeaheadValue(type);

        if (key == null) {
            return;
        }

        const entries = lookup.filter((item) => item.key === key);
        const hasEmployee = entries.some((entry) => getTypeaheadValue(entry) === employeeId);

        if (hasEmployee) {
            labels.push(getTypeaheadLabel(type));
        }
    });

    return labels;
};

export const getEmployeeAddress = (emp) => {
    const addr = emp?.homeAddress;
    if (!addr) return null;

    const parts = [
        addr.address1,
        addr.address2,
        [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : null;
};

export const getEmployeeMapDataForEmployee = (empId, mapData) => {
    if (!mapData || empId == null) {
        return null;
    }

    const attendanceTypes = mapData.dailyAttendanceEvents ?? [];
    const cancellationTypes = mapData.jobCancellationTypes ?? [];
    const longTermTypes = mapData.longTermStatuses ?? [];
    const attendanceLookup = mapData.employeesByDailyAttendanceEvent;
    const cancellationLookup = mapData.employeesByJobAssignmentCancellation;
    const longTermLookup = mapData.employeesByLongTermStatus;
    const scheduledSet = buildEmployeeSet(mapData.scheduled);
    const notScheduledSet = buildEmployeeSet(mapData.notScheduled);
    const confirmedSet = buildEmployeeSet(mapData.confirmed);
    const notConfirmedSet = buildEmployeeSet(mapData.notConfirmed);

    const crewStatuses = Array.isArray(mapData.crewStatuses) ? mapData.crewStatuses : [];
    const jobNumbers = Array.from(new Set(
        crewStatuses
            .filter((status) => status?.employeeId === empId)
            .map((status) => status?.jobNumber)
            .filter(Boolean),
    ));

    const attendance = collectTypeLabelsForEmployee(
        attendanceTypes,
        attendanceLookup,
        empId,
    );
    const cancellations = collectTypeLabelsForEmployee(
        cancellationTypes,
        cancellationLookup,
        empId,
    );
    const longTerm = collectTypeLabelsForEmployee(
        longTermTypes,
        longTermLookup,
        empId,
    );

    let scheduleStatus = 'Unknown';
    if (scheduledSet.has(empId)) scheduleStatus = 'Scheduled';
    if (notScheduledSet.has(empId)) scheduleStatus = 'Not Scheduled';

    let confirmationStatus = 'Unknown';
    if (confirmedSet.has(empId)) confirmationStatus = 'Confirmed';
    if (notConfirmedSet.has(empId)) confirmationStatus = 'Not Confirmed';

    const certifications = collectTypeLabelsForEmployee(
        mapData.certifications ?? [],
        mapData.employeesByEmployeeCertification,
        empId,
    );
    const userGroups = collectTypeLabelsForEmployee(
        mapData.userGroups ?? [],
        mapData.employeesByUserGroup,
        empId,
    );

    return {
        attendance,
        cancellations,
        longTerm,
        scheduleStatus,
        confirmationStatus,
        jobNumbers,
        certifications,
        userGroups,
    };
};

export const getEmployeeId = (emp) => (emp ? emp.employeeId ?? emp.id ?? null : null);

export const getEmpCoords = (emp, dispatchLocations = []) => {
    if (!emp) return null;

    const homeLat = emp.homeAddress?.latitude ?? emp.latitude ?? null;
    const homeLng = emp.homeAddress?.longitude ?? emp.longitude ?? null;

    if (homeLat != null && homeLng != null) {
        return { lat: homeLat, lng: homeLng };
    }

    const dispatchName = emp.dispatchLocation || emp.dispatchCompany;
    if (dispatchName && Array.isArray(dispatchLocations)) {
        const dispatchLoc = dispatchLocations.find((loc) => loc.label === dispatchName);
        if (dispatchLoc) {
            const lat = dispatchLoc.latitude ?? null;
            const lng = dispatchLoc.longitude ?? null;
            if (lat != null && lng != null) {
                return { lat: Number(lat), lng: Number(lng) };
            }
        }
    }

    return null;
};

export const EmployeeIndicatorLegend = [
    {
        icon: faUserTag, color: '#0734ff', title: 'Daily attendance event', key: 'daily-attendance-event',
    },
    {
        icon: faCalendarXmark, color: '#dc3545', title: 'Job cancellation', key: 'job-cancellation',
    },
    {
        icon: faClock, color: '#ffc107', title: 'Long term status', key: 'long-term-status',
    },
    {
        icon: faUserXmark, color: '#dc3545', title: 'Not confirmed', key: 'not-confirmed',
    },
    {
        icon: faCalendarPlus, color: '#28a745', title: 'Not scheduled', key: 'not-scheduled',
    },
    {
        icon: faSquareCheck, color: '#0d6efd', title: 'Confirmed', key: 'confirmed',
    },
    {
        icon: faPhone, color: '#28a745', title: 'On call', key: 'on-call',
    },
];